import React, { memo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTimeout } from 'ahooks';
import { CnIcon } from '../cn-icon';
import { withNativeProps } from '@/utils/with-native-props';
import { useResizeEffect } from '../../hooks/use-resize-effect';
import { useMutationEffect } from '../../hooks/use-mutation-effect';
const clsPrefix = 'cn-ui-m-notice';
const iconTypeMap = {
    notice: 'horn-fill',
    warning: 'exclamation-t-fill',
    success: 'complete-r-fill',
    error: 'error-r-fill',
    help: 'exclamation-r-fill',
};
const defaultProps = {
    type: 'notice',
    delay: 2000,
    speed: 50,
    closeable: false,
    overflowStyle: 'scroll',
    icon: React.createElement(CnIcon, { type: "horn-fill" }),
};
// eslint-disable-next-line react/display-name
export const CnNotice = memo((p) => {
    const props = {
        ...defaultProps,
        icon: React.createElement(CnIcon, { type: iconTypeMap[p.type ?? 'notice'] }),
        ...p,
    };
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const [visible, setVisible] = useState(true);
    const { speed, overflowStyle, onClick } = props;
    const delayLockRef = useRef(true);
    const animatingRef = useRef(false);
    function start() {
        if (overflowStyle === 'hidden') {
            return;
        }
        if (delayLockRef.current)
            return;
        const container = containerRef.current;
        const text = textRef.current;
        if (!container || !text)
            return;
        if (container.offsetWidth >= text.offsetWidth) {
            animatingRef.current = false;
            text.style.removeProperty('transition-duration');
            text.style.removeProperty('transform');
            return;
        }
        if (animatingRef.current)
            return;
        const initial = !text.style.transform;
        text.style.transitionDuration = '0s';
        if (initial) {
            text.style.transform = 'translateX(0)';
        }
        else {
            text.style.transform = `translateX(${container.offsetWidth}px)`;
        }
        const distance = initial
            ? text.offsetWidth
            : container.offsetWidth + text.offsetWidth;
        animatingRef.current = true;
        text.style.transitionDuration = `${Math.round(distance / speed)}s`;
        text.style.transform = `translateX(-${text.offsetWidth}px)`;
    }
    useResizeEffect(() => {
        start();
    }, containerRef);
    useTimeout(() => {
        delayLockRef.current = false;
        start();
    }, props.delay);
    useMutationEffect(() => {
        start();
    }, textRef, {
        subtree: true,
        childList: true,
        characterData: true,
    });
    if (!visible)
        return null;
    return withNativeProps(props, React.createElement("div", { className: classNames(clsPrefix, `${clsPrefix}-${props.type}`, `${clsPrefix}-${overflowStyle}`), onClick: onClick },
        props.icon && React.createElement("span", { className: `${clsPrefix}-left` }, props.icon),
        React.createElement("span", { ref: containerRef, className: `${clsPrefix}-content` },
            React.createElement("span", { onTransitionEnd: () => {
                    animatingRef.current = false;
                    start();
                }, ref: textRef, className: `${clsPrefix}-content-inner` }, props.content)),
        overflowStyle === 'hidden' ? (React.createElement(CnIcon, { type: "triangle-right-fill", size: "small", outerClassName: classNames(`${clsPrefix}-action`) })) : null,
        (props.closeable || props.extra) && (React.createElement("span", { className: classNames(`${clsPrefix}-right`, {
                [`${clsPrefix}-right-with-icon`]: overflowStyle === 'hidden',
            }) },
            props.extra,
            props.closeable && (React.createElement("div", { className: `${clsPrefix}-close`, onClick: (event) => {
                    setVisible(false);
                    props.onClose?.();
                    event.stopPropagation();
                } },
                React.createElement(CnIcon, { type: "close", size: "small", className: classNames(`${clsPrefix}-close`) })))))));
});
CnNotice.displayName = 'CnNotice';
